import {
  Box,
  Button,
  Divider,
  Grid,
  GridItem,
  HStack,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  StackDivider,
  Switch,
  Text,
} from "@chakra-ui/react";
import { Trans, t } from "@lingui/macro";
import { getBillingCategoryOptions } from "@src/components/modules/projects/list/Store/Store";
import type { TaskListingBulkActionsStore } from "@src/components/modules/resource-planning/tasks/listing/store/bulk-actions";
import {
  FormRow,
  InputDatePicker,
  type IOption,
  NumberInput,
  RemoveButton,
  Select,
} from "@src/components/ui-kit";
import { Dash } from "@src/components/ui-kit/Dash";
import { Icon } from "@src/components/ui-kit/Icon";
import { TextEditor } from "@src/components/ui-kit/TextEditor/TextEditor";
import { PositionMappingSection } from "@src/components/widgets/Modals/ModalTask/PositionMappingSection";
import { WrapComponent } from "@src/utils/components/WrapComponent";
import { useStore } from "@src/utils/hooks";
import { BudgetItemSelect } from "@src/widgets/budget-item-select";
import { ProjectSelect } from "@src/widgets/ProjectSelect/ProjectSelect";
import { TaskSelect } from "@src/widgets/TaskSelect/TaskSelect";
import { UserSelect } from "@src/widgets/UserSelect";
import { observer } from "mobx-react-lite";
import { Fragment, useEffect } from "react";

type TasksBulkActionsModalProps = {
  store: TaskListingBulkActionsStore;
  defaultProjectId?: string;
  defaultProjectOptions?: IOption[];
  defaultBudgetItemId?: string;
  defaultBudgetItemOptions?: IOption[];
};

export const TasksBulkActionsModal = observer(function TasksBulkActionsModal({
  store,
  defaultProjectId,
  defaultProjectOptions,
  defaultBudgetItemId,
  defaultBudgetItemOptions,
}: TasksBulkActionsModalProps) {
  const { authStore } = useStore();

  useEffect(() => {
    if (!store.isModalOpen) return;
    if (!defaultProjectId) return;
    store.handleChangeProjectId(defaultProjectId);
  }, [store.isModalOpen, defaultProjectId]);

  useEffect(() => {
    if (!store.isModalOpen) return;
    if (!defaultBudgetItemId) return;
    store.handleChangeBudgetItemId(defaultBudgetItemId);
  }, [store.isModalOpen, defaultBudgetItemId]);

  return (
    <Modal
      isCentered
      isOpen={store.isModalOpen}
      onClose={store.handleModalClose}
      scrollBehavior="inside"
      size={store.actionType === "delete" ? "xl" : "4xl"}
      trapFocus={false}
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          {store.actionType === "edit" && t`Edit selected tasks`}
          {store.actionType === "duplicate" && t`Duplicate selected tasks`}
          {store.actionType === "move" && t`Move selected tasks`}
          {store.actionType === "delete" && t`Delete selected tasks`}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {store.actionType !== "delete" ? (
            <Fragment>
              <FormRow title={t`Title`}>
                <Input
                  onChange={(e) =>
                    store.form.$.name.onChange(e.target.value ?? undefined)
                  }
                  placeholder={t`Don't change`}
                  value={store.form.$.name.value ?? ""}
                />
              </FormRow>

              <FormRow title={t`Project`}>
                <ProjectSelect
                  asPortal
                  options={[]}
                  defaultOptions={defaultProjectOptions}
                  placeholder={t`Don’t change (or select to change Budget Item)`}
                  onChange={store.handleChangeProjectId}
                  value={store.form.$.project_id.value}
                  label={undefined}
                  isClearable
                />
              </FormRow>

              <FormRow title={t`Budget Item`}>
                <BudgetItemSelect
                  asPortal
                  label={undefined}
                  placeholder={t`Don't change`}
                  defaultOptions={defaultBudgetItemOptions}
                  projectId={store.form.$.project_id.value}
                  onChange={store.handleChangeBudgetItemId}
                  value={store.form.$.our_work_budget_item_id.value ?? ""}
                  isClearable
                  error={store.form.$.our_work_budget_item_id.error}
                />
              </FormRow>

              <FormRow title={t`Parent task`}>
                <TaskSelect
                  options={[]}
                  asPortal
                  isClearable
                  parentTasksOnly
                  withCreateTask={false}
                  value={store.form.$.parent_task_id.value}
                  projectFilter={
                    store.form.$.project_id.value
                      ? [store.form.$.project_id.value]
                      : []
                  }
                  budgetItemFilter={
                    store.form.$.our_work_budget_item_id.value
                      ? [store.form.$.our_work_budget_item_id.value]
                      : []
                  }
                  onChange={(value) => {
                    store.form.$.parent_task_id.onChange(
                      Array.isArray(value) ? value?.[0] : value,
                    );
                  }}
                  placeholder={t`Don't change`}
                />
              </FormRow>

              <FormRow title={t`Description`}>
                <Box
                  sx={{
                    border: "1px solid",
                    borderColor: "grey.200",
                    rounded: "lg",
                    p: "1",
                  }}
                >
                  <TextEditor
                    attachments={[]}
                    ref={store.descriptionInputRef}
                    showToolbar
                    ml="2"
                    minH="80px"
                    isEditable
                    onChange={store.handleChangeDescription}
                    placeholder={t`Don't change`}
                  />
                </Box>
              </FormRow>

              {store.actionType !== "move" && (
                <Fragment>
                  <Divider />

                  <FormRow title="" pt="4">
                    <WrapComponent
                      if={authStore.isInternalUser}
                      with={(children) => (
                        <Stack
                          direction={{ base: "column", md: "row" }}
                          divider={<StackDivider />}
                          spacing="3"
                        >
                          {children}
                        </Stack>
                      )}
                      elseWith={(children) => (
                        <HStack align="start" spacing="4">
                          {children}
                        </HStack>
                      )}
                    >
                      <Stack
                        w={{ base: "full", md: "60%" }}
                        dir="column"
                        spacing="3"
                      >
                        {authStore.isInternalUser && (
                          <HStack>
                            <InputDatePicker
                              flex="1"
                              label={t`Duration`}
                              selected={store.form.$.from.value}
                              onChange={(value) =>
                                store.form.$.from.onChange(value?.start)
                              }
                              placeholder={t`Don't change`}
                              error={store.form.$.from.error}
                            />
                            <Dash h="8" alignSelf="end" />
                            <InputDatePicker
                              flex="1"
                              label={t`To`}
                              selected={store.form.$.to.value}
                              onChange={(value) => {
                                store.form.$.to.onChange(value?.start);
                              }}
                              error={store.form.$.to.error}
                              placeholder={t`Don't change`}
                            />
                          </HStack>
                        )}
                        <HStack alignItems="start" gap="2">
                          <Select
                            label={t`Status`}
                            asPortal
                            error={store.form.$.task_status_id.error}
                            value={store.form.$.task_status_id.$}
                            options={store.taskStatusOptions}
                            onChange={store.form.$.task_status_id.onChange}
                            menuShouldScrollIntoView={false}
                            isClearable
                            placeholder={t`Don't change`}
                            isLoading={store.isFetchingOptions}
                          />
                          {authStore.isInternalUser && (
                            <Select
                              placeholder={t`Don't change`}
                              label={t`Billing category`}
                              asPortal
                              error={store.form.$.billable.error}
                              value={store.form.$.billable.$}
                              options={getBillingCategoryOptions()}
                              onChange={store.form.$.billable.onChange}
                              menuShouldScrollIntoView={false}
                              isClearable
                            />
                          )}
                        </HStack>
                      </Stack>
                      <Stack
                        w={{ base: "full", md: "40%" }}
                        dir="column"
                        spacing="3"
                      >
                        {authStore.isInternalUser && (
                          <InputDatePicker
                            label={t`Deadline`}
                            maxW="315px"
                            selected={
                              store.form.$.deadline.value
                                ? new Date(store.form.$.deadline.value)
                                : undefined
                            }
                            onChange={(val) => {
                              store.form.$.deadline.onChange(val?.start);
                            }}
                            error={store.form.$.deadline.error}
                            placeholder={t`Don't change`}
                          />
                        )}
                        <Select
                          label={t`Priority`}
                          error={store.form.$.task_priority_id.error}
                          asPortal
                          value={store.form.$.task_priority_id.$}
                          options={store.taskPriorityOptions}
                          onChange={store.form.$.task_priority_id.onChange}
                          menuShouldScrollIntoView={false}
                          isClearable
                          placeholder={t`Don't change`}
                        />
                      </Stack>
                    </WrapComponent>
                  </FormRow>
                </Fragment>
              )}

              <Divider />

              <FormRow title={t`Delete old positions`} pt="4">
                <Switch
                  isChecked={store.form.$.delete_old_positions.value}
                  onChange={(e) =>
                    store.form.$.delete_old_positions.onChange(e.target.checked)
                  }
                />
              </FormRow>

              <FormRow
                title={t`Add new positions`}
                style={{ alignItems: "start" }}
                titleClassName="mt-4"
              >
                <Grid
                  as="tr"
                  alignItems="center"
                  columnGap="1rem"
                  templateColumns="22fr 35fr 3fr 35fr 5fr"
                  w="100%"
                  color="#637381"
                  fontSize="xs"
                >
                  <GridItem>
                    <Trans>Total hrs</Trans>
                  </GridItem>
                  <GridItem>
                    <Trans>Position</Trans>
                  </GridItem>
                  <GridItem></GridItem>
                  <GridItem>
                    <Trans>Person</Trans>
                  </GridItem>
                  <GridItem></GridItem>
                </Grid>
                {store.form.$.add_task_positions.$.map(
                  (positionForm, index) => (
                    <Grid
                      key={`position-form-${
                        // biome-ignore lint/suspicious/noArrayIndexKey: Position form has no unique identifier
                        index
                      }`}
                      as="tr"
                      alignItems="start"
                      columnGap="1rem"
                      templateColumns="22fr 35fr 3fr 35fr 5fr"
                      mt="2"
                    >
                      <GridItem>
                        <NumberInput
                          minValue={0}
                          value={positionForm.$.planned_time.value}
                          onChange={positionForm.$.planned_time.onChange}
                        />
                      </GridItem>
                      <GridItem isTruncated>
                        <Select
                          asPortal
                          value={
                            positionForm.$.time_tracking_work_type_id.value
                          }
                          onChange={
                            positionForm.$.time_tracking_work_type_id.onChange
                          }
                          options={store.workTypeOptions}
                          noOptionsMessage={() =>
                            store.form.$.our_work_budget_item_id.value
                              ? t`No work types found`
                              : t`Select budget item first.`
                          }
                          error={
                            positionForm.$.time_tracking_work_type_id.error
                          }
                        />
                      </GridItem>
                      <GridItem alignSelf="center" color="gray.300">
                        /
                      </GridItem>
                      <GridItem isTruncated>
                        <UserSelect
                          asPortal
                          isClearable
                          placeholder={t`Unassigned`}
                          value={positionForm.$.user_id.value}
                          onChange={positionForm.$.user_id.onChange}
                          error={positionForm.$.user_id.error}
                        />
                      </GridItem>
                      <GridItem>
                        <RemoveButton
                          onClick={() => store.removePosition(index)}
                        />
                      </GridItem>
                    </Grid>
                  ),
                )}
                <Grid
                  as="tr"
                  alignItems="center"
                  columnGap="1rem"
                  templateColumns="22fr 35fr 3fr 35fr 5fr"
                  mt="2"
                >
                  <NumberInput
                    value={store.positionsTotalHours}
                    onChange={() => {}}
                    disabled
                  />
                  <Box>
                    <Button
                      mt="1"
                      pl="0"
                      color="grey.600"
                      leftIcon={<Icon name="plus" />}
                      onClick={store.addPosition}
                      size="sm"
                      variant="plain"
                    >
                      {t`Add`}
                    </Button>
                  </Box>
                </Grid>
              </FormRow>
              <PositionMappingSection store={store.positionMappingStore} />
            </Fragment>
          ) : (
            // Confirmation for delete action
            <Text>{t`Are you sure you want to delete the selected tasks? This action cannot be undone.`}</Text>
          )}
        </ModalBody>

        <ModalFooter>
          <Button
            mr="3"
            colorScheme="grey"
            onClick={store.handleModalClose}
            variant="ghost"
          >
            <Trans>Cancel</Trans>
          </Button>
          <Button
            colorScheme={store.actionType === "delete" ? "red" : undefined}
            isLoading={store.isLoading}
            onClick={store.handleConfirm}
          >
            {store.actionType === "delete" ? (
              <Trans>Delete</Trans>
            ) : (
              <Trans>Confirm</Trans>
            )}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
});
